/**
 * **Brand** — akar dari segalanya di tenant ini.
 *
 * ### ⭐⭐ Kenapa layar ini akhirnya dibuat (Imron, 2026-08-03)
 * Satu tenant boleh punya **beberapa brand** — grup Arena memang begitu. Sampai kemarin brand hanya
 * bisa lahir lewat importir atau SQL: pemilik yang membuka merek kedua tak punya jalan sama sekali,
 * dan seluruh layar master yang dimulai dari pemilih brand jadi buntu di langkah pertama.
 *
 * ### ⛔ Kode brand BEKU, dan `business_type` ikut beku
 * `code` sudah tercetak di struk dan dipakai importir. `business_type` menentukan bentuk yang
 * diasumsikan modul lain (F&B punya meja & dapur; retail tidak) — mengubahnya sesudah ada
 * transaksi berarti menafsir ulang data yang sudah terjadi. Server memang tak menerima keduanya
 * di `update`.
 */
import { Alert, Form, Input, InputNumber, Select, Space, Switch, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Brand } from '../api/client';
import { canWrite } from '../api/session';
import { CrudPanel } from '../components/CrudPanel';
import { FormTabs } from '../components/FormTabs';

const BUSINESS_TYPES = ['fnb', 'retail', 'service'] as const;

/**
 * Kebijakan brand ↔ formulir.
 *
 * ⚠️ Bawaannya ditulis SATU KALI di sini, dan angkanya sama dengan `DEFAULT` di DDL (0046). Ia
 * hanya dipakai untuk brand yang barisnya belum membawa nilai — bukan untuk "menebak" saat server
 * mengirim `0` atau `false`, yang keduanya jawaban sah.
 */
const POLICY_DEFAULT: Readonly<Record<string, number | boolean>> = Object.freeze({
  reprintWindowMinutes: 60,
  cashVarianceThreshold: 0,
  allowReopenAfterCashTender: false,
  drawerOpenReviewThreshold: 3,
  guestCallCooldownSeconds: 30,
  guestCallSpamThreshold: 3,
  guestCallSuspendMinutes: 60,
  guestCallRequiresOpenShift: true,
  printZeroPriceModifiers: true,
  removeSentLineWindowMinutes: 0,
});

/** Baris brand → nilai formulir. ⛔ `??`, bukan `||`: `0` & `false` adalah jawaban. */
function policyForm(b: Brand): Record<string, unknown> {
  return {
    reprintWindowMinutes: b.reprint_window_minutes ?? POLICY_DEFAULT['reprintWindowMinutes'],
    cashVarianceThreshold: Number(b.cash_variance_threshold ?? 0),
    allowReopenAfterCashTender:
      b.allow_reopen_after_cash_tender ?? POLICY_DEFAULT['allowReopenAfterCashTender'],
    drawerOpenReviewThreshold:
      b.drawer_open_review_threshold ?? POLICY_DEFAULT['drawerOpenReviewThreshold'],
    guestCallCooldownSeconds:
      b.guest_call_cooldown_seconds ?? POLICY_DEFAULT['guestCallCooldownSeconds'],
    guestCallSpamThreshold: b.guest_call_spam_threshold ?? POLICY_DEFAULT['guestCallSpamThreshold'],
    guestCallSuspendMinutes:
      b.guest_call_suspend_minutes ?? POLICY_DEFAULT['guestCallSuspendMinutes'],
    guestCallRequiresOpenShift:
      b.guest_call_requires_open_shift ?? POLICY_DEFAULT['guestCallRequiresOpenShift'],
    printZeroPriceModifiers:
      b.print_zero_price_modifiers ?? POLICY_DEFAULT['printZeroPriceModifiers'],
    removeSentLineWindowMinutes:
      b.remove_sent_line_window_minutes ?? POLICY_DEFAULT['removeSentLineWindowMinutes'],
  };
}

/**
 * Angka kebijakan dari formulir, dengan bawaan bila kosong.
 *
 * ⛔⛔ **Bukan `Number(v)` telanjang, dan itu memperbaiki DUA bug sekaligus:**
 *
 * 1. `Number(undefined)` = `NaN`, dan `JSON.stringify(NaN)` = **`null`** ⇒ server menjawab
 *    "Expected number, received null". Terjadi tiap kali brand BARU dibuat, karena laci
 *    buat-baru dulu lahir dengan seluruh `InputNumber` kosong.
 * 2. Jauh lebih jahat: `Number(null)` = **`0`**, dan AntD memberi `null` untuk `InputNumber` yang
 *    DIKOSONGKAN. Jadi menghapus isi "jeda panggilan tamu" akan tersimpan sebagai **0 detik** —
 *    perlindungan spam MATI, tanpa satu pun galat. ⚠️ `0` adalah jawaban yang sah bagi sebagian
 *    kebijakan ini, jadi tak ada apa pun di hilir yang bisa mencurigainya.
 *
 * ⇒ Kosong ⇒ **bawaan pabrik**, bukan nol. Dan `required` di formulir membuat keadaan itu praktis
 *   tak tercapai — helper ini jaring terakhir, bukan jaring pertama.
 */
function angka(v: unknown, bawaan: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : bawaan;
}

/** Nilai formulir → badan permintaan. ⚠️ Uang dikirim STRING berskala 4 (P1). */
function policyBody(v: Record<string, unknown>): Record<string, unknown> {
  const d = POLICY_DEFAULT;
  return {
    reprintWindowMinutes: angka(v['reprintWindowMinutes'], Number(d['reprintWindowMinutes'])),
    cashVarianceThreshold: `${angka(v['cashVarianceThreshold'], 0).toFixed(4)}`,
    allowReopenAfterCashTender: v['allowReopenAfterCashTender'] === true,
    drawerOpenReviewThreshold: angka(v['drawerOpenReviewThreshold'], Number(d['drawerOpenReviewThreshold'])),
    guestCallCooldownSeconds: angka(v['guestCallCooldownSeconds'], Number(d['guestCallCooldownSeconds'])),
    guestCallSpamThreshold: angka(v['guestCallSpamThreshold'], Number(d['guestCallSpamThreshold'])),
    guestCallSuspendMinutes: angka(v['guestCallSuspendMinutes'], Number(d['guestCallSuspendMinutes'])),
    guestCallRequiresOpenShift: v['guestCallRequiresOpenShift'] === true,
    printZeroPriceModifiers: v['printZeroPriceModifiers'] === true,
    removeSentLineWindowMinutes: angka(v['removeSentLineWindowMinutes'], 0),
  };
}

export function Brands() {
  const { t } = useTranslation();
  const boleh = canWrite();

  /* ⛔ `includeInactive`: layar pengelola — brand yang dimatikan harus tetap bisa dinyalakan. */
  const muat = useCallback(() => api.brands(true), []);

  const kolom: ColumnsType<Brand> = [
    {
      title: t('master.code'),
      dataIndex: 'code',
      width: 160,
      render: (v: string) => <b data-testid={`brand-${v}`}>{v}</b>,
    },
    { title: t('master.name'), dataIndex: 'name' },
    {
      title: t('brands.businessType'),
      dataIndex: 'business_type',
      width: 120,
      render: (v: string) => <Tag color={v === 'fnb' ? 'green' : 'default'}>{v}</Tag>,
    },
    {
      title: '',
      dataIndex: 'is_active',
      width: 96,
      render: (v: boolean) =>
        v ? <Tag color="green">{t('outlets.active')}</Tag> : <Tag>{t('outlets.inactive')}</Tag>,
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="brands">
      <Alert type="info" showIcon title={t('brands.note')} data-testid="brand-catatan" />

      <CrudPanel<Brand>
        testId="brand"
        title={t('brands.title')}
        subtitle={t('brands.subtitle')}
        canWrite={boleh}
        newLabel={t('brands.new')}
        columns={kolom}
        fetchList={muat}
        searchIn={(b) => `${b.code} ${b.name}`}
        emptyText={t('brands.empty')}
        renderFields={(_f, editing) => (
          /*
           * ⭐ Tab, bukan satu laci panjang — polanya SAMA dengan layar Outlet.
           *
           * ⛔ Laci yang isinya ditumpuk membuat kolom yang menentukan (jenis usaha, ambang dapur)
           * hanyut di tengah gulungan, dan yang membacanya berhenti membaca. `FormTabs` memasang
           * `forceRender` di tiap panel, jadi tab di sini murni TATA LETAK: yang divalidasi dan
           * yang terkirim persis sama dengan sebelumnya.
           *
           * ⚠️ `names` wajib lengkap — dari situ galat dipetakan ke tabnya. Nama yang lupa
           * didaftarkan tetap ditolak server, tapi penandanya tak menyala dan tombol Simpan
           * terlihat seperti tak melakukan apa-apa.
           */
          <FormTabs
            testId="brand"
            items={[
              {
                key: 'identitas',
                label: t('outlets.tabIdentity'),
                names: ['code', 'businessType', 'name', 'logoUrl'],
                children: (
                  <>
                    {editing === null ? (
                      <>
                        <Form.Item
                          name="code"
                          label={t('master.code')}
                          rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}
                          tooltip={t('structure.codeFixed')}
                        >
                          <Input maxLength={40} data-testid="brand-kode" />
                        </Form.Item>
                        {/* ⭐⭐ Jenis usaha menyaring preset outlet nanti (K16) — dan ia BEKU
                            sesudah brand lahir, jadi tempatnya paling depan. */}
                        <Form.Item
                          name="businessType"
                          label={t('brands.businessType')}
                          initialValue="fnb"
                          tooltip={t('brands.businessTypeHint')}
                        >
                          <Select options={BUSINESS_TYPES.map((v) => ({ value: v, label: v }))} />
                        </Form.Item>
                      </>
                    ) : (
                      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t('brands.frozen')} />
                    )}

                    <Form.Item name="name" label={t('master.name')} rules={[{ required: true, min: 2 }]}>
                      <Input maxLength={120} data-testid="brand-nama" />
                    </Form.Item>

                    {/* ⚠️ Logo tampil di layar login & chrome kasir. Kosong ⇒ nama brand dalam huruf besar. */}
                    <Form.Item name="logoUrl" label={t('brands.logo')} tooltip={t('brands.logoHint')}
                               rules={[{ type: 'url', message: t('brands.logoRule') }]}>
                      <Input maxLength={500} placeholder="https://…" data-testid="brand-logo" />
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'dapur',
                label: t('outlets.tabKitchen'),
                names: ['kdsWarnMinutes', 'kdsLateMinutes'],
                children: (
                  <>
                    {/*
                      ⭐⭐ Ambang usia tiket dapur — disetel DI SINI, bukan per outlet, karena "berapa lama
                      pantas" adalah keputusan KONSEP, bukan keputusan gedung: seluruh outlet satu brand
                      memasak menu yang sama dengan dapur yang dirancang sama. Menyetelnya per outlet
                      berarti 47 outlet mengisi angka yang sama satu per satu — dan yang terjadi berikutnya
                      adalah 47 angka berbeda tanpa ada yang memutuskannya.
                      ⚠️ Outlet tetap boleh MENIMPA-nya (layar Outlet) bila dapurnya memang beda.
                    */}
                    <Space align="start">
                      <Form.Item name="kdsWarnMinutes" label={t('brands.kdsWarn')}
                                 tooltip={t('brands.kdsWarnHint')}>
                        <InputNumber min={1} max={240} data-testid="brand-kds-warn" />
                      </Form.Item>
                      <Form.Item name="kdsLateMinutes" label={t('brands.kdsLate')}>
                        <InputNumber min={2} max={480} data-testid="brand-kds-late" />
                      </Form.Item>
                    </Space>
                  </>
                ),
              },
              {
                key: 'kebijakan',
                label: t('policy.tab'),
                names: ['reprintWindowMinutes', 'cashVarianceThreshold', 'allowReopenAfterCashTender',
                  'drawerOpenReviewThreshold', 'printZeroPriceModifiers',
                  'removeSentLineWindowMinutes', 'guestCallCooldownSeconds',
                  'guestCallSpamThreshold', 'guestCallSuspendMinutes', 'guestCallRequiresOpenShift'],
                children: (
                  <>
                    {/*
                      ⭐⭐⭐ Kebijakan disetel DI SINI, bukan per outlet — alasannya sama persis
                      dengan ambang KDS: "berapa lama struk boleh dicetak ulang" adalah keputusan
                      KONSEP, bukan keputusan gedung. Menyetelnya per outlet berarti 47 outlet
                      mengisi angka yang sama satu per satu, lalu jadi 47 angka berbeda tanpa ada
                      yang pernah memutuskannya.
                      ⚠️ Outlet tetap boleh MENIMPA tiap barisnya (layar Outlet → tab Kebijakan).
                    */}
                    <Alert type="info" showIcon style={{ marginBottom: 16 }}
                           title={t('policy.brandHint')} data-testid="brand-kebijakan-catatan" />

                    <Typography.Text strong>{t('policy.groupCash')}</Typography.Text>
                    <Space align="start" wrap style={{ marginBottom: 12 }}>
                      <Form.Item name="cashVarianceThreshold" label={t('policy.cashVariance')}
                                 tooltip={t('policy.cashVarianceHint')}>
                        <InputNumber min={0} step={1000} data-testid="pol-cash-variance" />
                      </Form.Item>
                      <Form.Item rules={[{ required: true }]} name="drawerOpenReviewThreshold" label={t('policy.drawerReview')}
                                 tooltip={t('policy.drawerReviewHint')}>
                        <InputNumber min={1} max={50} data-testid="pol-drawer-review" />
                      </Form.Item>
                    </Space>

                    <Typography.Text strong>{t('policy.groupBill')}</Typography.Text>
                    <Space align="start" wrap style={{ marginBottom: 12 }}>
                      <Form.Item rules={[{ required: true }]} name="reprintWindowMinutes" label={t('policy.reprintWindow')}
                                 tooltip={t('policy.reprintWindowHint')}>
                        <InputNumber min={1} max={1440} data-testid="pol-reprint-window" />
                      </Form.Item>
                      <Form.Item name="allowReopenAfterCashTender" label={t('outlets.allowReopenCash')}
                                 valuePropName="checked" tooltip={t('outlets.allowReopenCashHint')}>
                        <Switch data-testid="pol-reopen-tunai"
                                checkedChildren={t('common.on')} unCheckedChildren={t('common.off')} />
                      </Form.Item>
                    </Space>
                    {/* ⚠️ Peringatan berdiri sendiri: `tooltip` menjelaskan setelannya, ini menyebut RISIKONYA. */}
                    <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                           title={t('outlets.allowReopenCashWarning')} />

                    <Typography.Text strong>{t('policy.groupKitchen')}</Typography.Text>
                    <Space align="start" wrap style={{ marginBottom: 12 }}>
                      {/*
                        ⭐⭐⭐ SATU tombol untuk seluruh keputusan (0048). Dulu di sini ada saklar
                        + isian menit, dan pasangan itu melahirkan keadaan yang tak berarti
                        apa-apa: "on tapi 0", "off tapi 30".
                        ⚠️ Artinya dibaca DI SAMPING kotaknya, ikut berubah saat diketik —
                        penjelasan di tooltip dibaca sekali (kalau ikonnya diketuk) lalu tak pernah
                        lagi; yang di sebelah nilai terbaca tiap kali orang menatapnya.
                      */}
                      <Form.Item label={t('policy.removeSent')} tooltip={t('policy.removeSentHint')}>
                        <Space align="center">
                          <Form.Item name="removeSentLineWindowMinutes" noStyle>
                            <InputNumber min={0} max={1440} data-testid="pol-remove-sent-window" />
                          </Form.Item>
                          <Form.Item noStyle shouldUpdate>
                            {(form) => (
                              <Typography.Text
                                type={
                                  Number(form.getFieldValue('removeSentLineWindowMinutes') ?? 0) === 0
                                    ? 'secondary' : 'warning'
                                }
                                data-testid="pol-remove-sent-window-arti"
                              >
                                {Number(form.getFieldValue('removeSentLineWindowMinutes') ?? 0) === 0
                                  ? `→ ${t('policy.removeSentOff')}`
                                  : `→ ${t('policy.windowMeans', {
                                      n: Number(form.getFieldValue('removeSentLineWindowMinutes')),
                                    })}`}
                              </Typography.Text>
                            )}
                          </Form.Item>
                        </Space>
                      </Form.Item>
                      <Form.Item name="printZeroPriceModifiers" label={t('policy.printZeroMod')}
                                 valuePropName="checked" tooltip={t('policy.printZeroModHint')}>
                        <Switch data-testid="pol-print-zero-mod"
                                checkedChildren={t('common.on')} unCheckedChildren={t('common.off')} />
                      </Form.Item>
                    </Space>
                    {/* ⛔ Bukan "boleh hapus bebas" — disebut di layar supaya tak dibaca terbalik. */}
                    <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                           title={t('policy.removeSentWarning')} data-testid="pol-remove-sent-warning" />

                    <Typography.Text strong>{t('policy.groupGuest')}</Typography.Text>
                    <Space align="start" wrap>
                      <Form.Item rules={[{ required: true }]} name="guestCallCooldownSeconds" label={t('policy.callCooldown')}
                                 tooltip={t('policy.callCooldownHint')}>
                        <InputNumber min={0} max={3600} data-testid="pol-call-cooldown" />
                      </Form.Item>
                      <Form.Item rules={[{ required: true }]} name="guestCallSpamThreshold" label={t('policy.callSpam')}
                                 tooltip={t('policy.callSpamHint')}>
                        <InputNumber min={1} max={50} data-testid="pol-call-spam" />
                      </Form.Item>
                      <Form.Item rules={[{ required: true }]} name="guestCallSuspendMinutes" label={t('policy.callSuspend')}
                                 tooltip={t('policy.callSuspendHint')}>
                        <InputNumber min={1} max={1440} data-testid="pol-call-suspend" />
                      </Form.Item>
                      <Form.Item name="guestCallRequiresOpenShift" label={t('policy.callNeedsOpen')}
                                 valuePropName="checked" tooltip={t('policy.callNeedsOpenHint')}>
                        <Switch data-testid="pol-call-needs-open"
                                checkedChildren={t('common.on')} unCheckedChildren={t('common.off')} />
                      </Form.Item>
                    </Space>
                  </>
                ),
              },
              /*
               * ⚠️ Tab status hanya muncul saat MENYUNTING — brand yang belum ada tak bisa
               * dinonaktifkan, dan tab kosong yang selalu ada hanya melatih orang melewatinya.
               */
              ...(editing === null
                ? []
                : [{
                    key: 'status',
                    label: t('outlets.tabAdmin'),
                    names: ['isActive'],
                    children: (
                      <>
                        <Form.Item name="isActive" label={t('outlets.status')} valuePropName="checked">
                          <Switch
                            data-testid="brand-aktif"
                            checkedChildren={t('outlets.active')}
                            unCheckedChildren={t('outlets.inactive')}
                          />
                        </Form.Item>
                        {/* ⛔ Bukan kosmetik: brand mati menutup SELURUH outlet, menu, dan promonya. */}
                        <Alert type="warning" showIcon title={t('brands.deactivate')} />
                      </>
                    ),
                  }]),
            ]}
          />
        )}
        /*
         * ⭐ Laci BUAT BARU lahir dengan bawaan pabrik yang TERLIHAT — bukan kosong.
         * ⚠️ Angkanya sama dengan `DEFAULT` di DDL (0046); yang membuat brand jadi tahu angka apa
         *    yang sedang ia setujui, alih-alih mewarisinya tanpa pernah melihatnya.
         */
        createDefaults={{ ...POLICY_DEFAULT, kdsWarnMinutes: 8, kdsLateMinutes: 15 }}
        /* ⚠️ Disimpan DETIK, ditampilkan MENIT — orang berpikir dalam menit, mesin dalam detik. */
        toFormValues={(b) => ({
          name: b.name, logoUrl: b.logo_url ?? '', isActive: b.is_active,
          kdsWarnMinutes: Math.round(Number(b.kds_warn_seconds ?? 480) / 60),
          kdsLateMinutes: Math.round(Number(b.kds_late_seconds ?? 900) / 60),
          ...policyForm(b),
        })}
        onCreate={async (v) => {
          await api.createBrand({
            code: String(v['code']).toUpperCase(),
            name: String(v['name']),
            businessType: String(v['businessType']),
            ...(String(v['logoUrl'] ?? '') === '' ? {} : { logoUrl: String(v['logoUrl']) }),
            kdsWarnSeconds: Number(v['kdsWarnMinutes'] ?? 8) * 60,
            kdsLateSeconds: Number(v['kdsLateMinutes'] ?? 15) * 60,
            ...policyBody(v),
          });
        }}
        onUpdate={async (id, v, lama) => {
          await api.updateMaster('brands', id, {
            name: v['name'],
            logoUrl: String(v['logoUrl'] ?? '') === '' ? null : v['logoUrl'],
            kdsWarnSeconds: Number(v['kdsWarnMinutes'] ?? 8) * 60,
            kdsLateSeconds: Number(v['kdsLateMinutes'] ?? 15) * 60,
            ...policyBody(v),
          });
          if (v['isActive'] !== lama.is_active) {
            await api.setStatus('brands', id, v['isActive'] === true);
          }
        }}
      />

      <Typography.Text type="secondary">{t('brands.footer')}</Typography.Text>
    </Space>
  );
}
